/* ============================================================================
   analytics.js - Firebase Analytics (GA4) with opt-in consent
   ============================================================================ */

import { app, firebaseConfig, isConfigured } from "./firebase-config.js?v=20261007m";

const CONSENT_KEY = "ccj_analytics_consent";
const CONSENT_COOKIE = "ccj_ac";
const BANNER_ID = "ccj-analytics-consent";
const NEED_ATTR = "data-ccj-need-consent";

function readConsentCookie() {
  try {
    const match = document.cookie.match(/(?:^|;\s*)ccj_ac=([^;]+)/);
    const value = decodeURIComponent(match?.[1] || "")
      .trim()
      .toLowerCase();
    if (value === "granted" || value === "denied") return value;
  } catch (_) {}
  return null;
}

function writeConsentCookie(value) {
  try {
    const secure = location.protocol === "https:" ? ";Secure" : "";
    document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(value)};path=/;max-age=31536000;SameSite=Lax${secure}`;
  } catch (_) {}
}

function clearConsentCookie() {
  try {
    const secure = location.protocol === "https:" ? ";Secure" : "";
    document.cookie = `${CONSENT_COOKIE}=;path=/;max-age=0;SameSite=Lax${secure}`;
  } catch (_) {}
}

let analyticsInstance = null;
let initPromise = null;
let wiredGlobalClicks = false;
let consentArmTimer = null;

function hasDecidedConsentChrome() {
  if (getAnalyticsConsent() != null) return true;
  try {
    return document.documentElement.hasAttribute("data-ccj-analytics");
  } catch (_) {
    return false;
  }
}

/** Clear stored choice (used by ?reset-analytics=1). */
export function clearAnalyticsConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (_) {}
  try {
    sessionStorage.removeItem(CONSENT_KEY);
  } catch (_) {}
  clearConsentCookie();
  try {
    document.documentElement.removeAttribute("data-ccj-analytics");
  } catch (_) {}
  markConsentNeeded(false);
  dismissConsentBanner();
  analyticsInstance = null;
}

/** If the URL asks to reset, wipe consent and clean the query param. */
function consumeResetAnalyticsParam() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("reset-analytics") !== "1") return false;
    clearAnalyticsConsent();
    url.searchParams.delete("reset-analytics");
    const next = `${url.pathname}${url.search}${url.hash}`;
    history.replaceState(history.state, "", next);
    return true;
  } catch (_) {
    return false;
  }
}

export function getAnalyticsConsent() {
  try {
    const raw =
      localStorage.getItem(CONSENT_KEY) ||
      sessionStorage.getItem(CONSENT_KEY) ||
      readConsentCookie();
    const v = String(raw || "")
      .trim()
      .toLowerCase();
    if (v === "granted" || v === "denied") return v;
  } catch (_) {}
  return null;
}

export function hasAnalyticsConsent() {
  return getAnalyticsConsent() === "granted";
}

function markConsentNeeded(needed) {
  try {
    if (!needed || hasDecidedConsentChrome()) {
      document.documentElement.removeAttribute(NEED_ATTR);
      return;
    }
    document.documentElement.setAttribute(NEED_ATTR, "1");
  } catch (_) {}
}

function dismissConsentBanner(root = document) {
  const scope = root?.querySelectorAll ? root : document;
  scope
    .querySelectorAll?.(`#${BANNER_ID}, .analytics-consent`)
    ?.forEach((el) => {
      el.classList.remove("is-armed");
      el.remove();
    });
  const live = document.getElementById(BANNER_ID);
  if (live) {
    live.classList.remove("is-armed");
    live.remove();
  }
}

function armConsentBanner() {
  if (hasDecidedConsentChrome()) return;
  if (consentArmTimer != null) return;
  consentArmTimer = window.setTimeout(() => {
    consentArmTimer = null;
    if (hasDecidedConsentChrome()) {
      markConsentNeeded(false);
      dismissConsentBanner();
      return;
    }
    markConsentNeeded(true);
    if (!document.getElementById(BANNER_ID)) {
      renderConsentBanner();
    }
    const el = document.getElementById(BANNER_ID);
    if (!el) return;
    requestAnimationFrame(() => {
      if (hasDecidedConsentChrome()) {
        markConsentNeeded(false);
        dismissConsentBanner();
        return;
      }
      el.classList.add("is-armed");
    });
  }, 120);
}

/** @param {"granted"|"denied"} value */
export function setAnalyticsConsent(value) {
  const next = value === "granted" ? "granted" : "denied";
  try {
    localStorage.setItem(CONSENT_KEY, next);
  } catch (_) {}
  try {
    sessionStorage.setItem(CONSENT_KEY, next);
  } catch (_) {}
  writeConsentCookie(next);
  try {
    document.documentElement.setAttribute("data-ccj-analytics", next);
  } catch (_) {}
  markConsentNeeded(false);
  dismissConsentBanner();
  if (next === "granted") {
    void initAnalytics();
  } else {
    analyticsInstance = null;
  }
}

export function itemParams(product) {
  if (!product) return {};
  return {
    item_id: String(product.id || ""),
    item_name: String(product.name || "").slice(0, 100),
    item_category: String(product.category || "Other").slice(0, 100),
    price: Number(product.price_in_credits || 0),
    currency: "CREDITS",
  };
}

export async function initAnalytics() {
  if (!isConfigured || !app || !firebaseConfig.measurementId) return null;
  if (!hasAnalyticsConsent()) return null;
  if (analyticsInstance) return analyticsInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const mod = await import(
        "https://www.gstatic.com/firebasejs/10.12.2/firebase-analytics.js"
      );
      const supported = await mod.isSupported().catch(() => false);
      if (!supported || !hasAnalyticsConsent()) return null;
      analyticsInstance = mod.getAnalytics(app);
      return analyticsInstance;
    } catch (err) {
      console.warn("[CookieCodeJar] Analytics init skipped:", err);
      analyticsInstance = null;
      return null;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

export function track(name, params = {}) {
  if (!name || !hasAnalyticsConsent()) return;
  void (async () => {
    try {
      const instance = analyticsInstance || (await initAnalytics());
      if (!instance) return;
      const { logEvent } = await import(
        "https://www.gstatic.com/firebasejs/10.12.2/firebase-analytics.js"
      );
      const clean = {};
      for (const [k, v] of Object.entries(params || {})) {
        if (v == null || v === "") continue;
        if (typeof v === "string") clean[k] = v.slice(0, 100);
        else clean[k] = v;
      }
      logEvent(instance, String(name).slice(0, 40), clean);
    } catch (err) {
      console.warn("[CookieCodeJar] Analytics track failed:", err);
    }
  })();
}

export function trackViewItem(product) {
  const item = itemParams(product);
  if (!item.item_id) return;
  track("view_item", {
    currency: item.currency,
    value: item.price,
    items: [item],
    item_id: item.item_id,
    item_name: item.item_name,
    item_category: item.item_category,
    price: item.price,
  });
}

export function trackUnlockStart(product) {
  track("unlock_start", itemParams(product));
}

export function trackUnlockSuccess(product, charged) {
  const item = itemParams(product);
  const value = Number(charged ?? item.price ?? 0);
  track("purchase", {
    currency: "CREDITS",
    value,
    transaction_id: `${item.item_id}_${Date.now()}`.slice(0, 100),
    items: [{ ...item, quantity: 1, price: value }],
  });
  track("unlock_success", {
    ...item,
    value,
  });
}

export function trackUnlockFail(product, reason) {
  track("unlock_fail", {
    ...itemParams(product),
    reason: String(reason || "error").slice(0, 40),
  });
}

export function trackLikeItem(productOrId, liked) {
  const id = typeof productOrId === "string" ? productOrId : productOrId?.id;
  const name = typeof productOrId === "string" ? undefined : productOrId?.name;
  track(liked ? "like_item" : "unlike_item", {
    item_id: String(id || ""),
    item_name: name ? String(name).slice(0, 100) : undefined,
  });
}

export function trackDownload(product) {
  track("download_asset", itemParams(product));
}

export function trackAuth(mode) {
  if (mode === "signup") track("sign_up", { method: "password" });
  else track("login", { method: "password" });
}

export function trackSelectContent(contentType, itemId = "") {
  track("select_content", {
    content_type: String(contentType || "").slice(0, 40),
    item_id: String(itemId || "").slice(0, 100),
  });
}

function wireGlobalClickTracking() {
  if (wiredGlobalClicks) return;
  wiredGlobalClicks = true;
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;

      const kofi = t.closest('a[href*="ko-fi.com"]');
      if (kofi) {
        track("kofi_click", {
          link_url: String(kofi.getAttribute("href") || "").slice(0, 100),
          link_text: String(kofi.textContent || "")
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 80),
        });
        return;
      }

      const marked = t.closest("[data-analytics]");
      if (marked) {
        trackSelectContent(
          marked.getAttribute("data-analytics") || "click",
          marked.getAttribute("data-analytics-id") || ""
        );
      }
    },
    true
  );
}

function renderConsentBanner() {
  if (getAnalyticsConsent() != null) return;
  if (document.getElementById(BANNER_ID)) return;

  const el = document.createElement("div");
  el.id = BANNER_ID;
  el.className = "analytics-consent";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", "Analytics cookies");
  el.innerHTML = `
    <div class="analytics-consent__inner">
      <p class="analytics-consent__copy">
        Optional cookies help Cookie improve the jar's content.
        <a href="privacy.html#analytics">Privacy details</a>
      </p>
      <div class="analytics-consent__actions">
        <button type="button" class="btn btn--ghost btn--sm" data-consent="denied">Decline</button>
        <button type="button" class="btn btn--primary btn--sm" data-consent="granted">Accept</button>
      </div>
    </div>
  `;
  el.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-consent]");
    if (!btn) return;
    setAnalyticsConsent(btn.getAttribute("data-consent") === "granted" ? "granted" : "denied");
  });
  document.body.appendChild(el);
}

/**
 * Banner is CSS-hidden unless html[data-ccj-need-consent] is set.
 * That attribute is only set after we confirm storage has no choice.
 */
export function bootAnalytics() {
  wireGlobalClickTracking();
  consumeResetAnalyticsParam();
  const consent = getAnalyticsConsent();

  if (consent === "granted" || consent === "denied") {
    try {
      document.documentElement.setAttribute("data-ccj-analytics", consent);
    } catch (_) {}
    markConsentNeeded(false);
    dismissConsentBanner();
    if (consent === "granted") void initAnalytics();
    return;
  }

  // Confirmed: no stored choice. Banner DOM is created only inside armConsentBanner.
  armConsentBanner();
}

document.addEventListener("turbo:before-cache", () => {
  dismissConsentBanner();
});
document.addEventListener("turbo:before-render", (event) => {
  const newBody = event.detail?.newBody;
  if (newBody) dismissConsentBanner(newBody);
});
document.addEventListener("turbo:load", () => {
  if (hasDecidedConsentChrome()) {
    markConsentNeeded(false);
    dismissConsentBanner();
    const consent = getAnalyticsConsent();
    if (consent != null) {
      try {
        document.documentElement.setAttribute("data-ccj-analytics", consent);
      } catch (_) {}
    }
    return;
  }
  armConsentBanner();
});
window.addEventListener("pageshow", () => {
  if (getAnalyticsConsent() != null) {
    markConsentNeeded(false);
    dismissConsentBanner();
    try {
      document.documentElement.setAttribute("data-ccj-analytics", getAnalyticsConsent());
    } catch (_) {}
  }
});

(function syncConsentChromeFromStorage() {
  const consent = getAnalyticsConsent();
  if (!consent) return;
  try {
    document.documentElement.setAttribute("data-ccj-analytics", consent);
    document.documentElement.removeAttribute(NEED_ATTR);
  } catch (_) {}
})();
